/*
 * THESIS: the catalogue is a console, not a document; one service is on the
 *   bench at a time, fully specified, refusing the long scroll of stacked
 *   panels this category ships.
 * OWN-WORLD: Maestro control-room surfaces on the flat graphite field, three
 *   accents only: cyan strategy and system, orange facilitation and urgency,
 *   lime engineering and value. Hairline dividers, ledger rows, stroke glyphs.
 * STORY: a buyer sees all nineteen services at a glance on the rail,
 *   recognizes their pain, selects, reads one spec sheet, opens a card.
 * FIRST VIEWPORT: compact hero (kicker, headline, one line, one quiet proof
 *   ledger line), then the workbench: rail left with every service named,
 *   stage right with the selected work order. Primary action on the sheet.
 * FORM: console workbench, candidate 3 of the grounded list, seed ceb3a9b0;
 *   staging: two-pane with inline expansion below lg.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getCtaContent } from '../../content/ctaContent'
import { getServicesCatalogContent } from '../../content/servicesCatalogContent'

// The brand's three accents, and only these: lime carries value and delivery,
// orange carries facilitation and urgency, cyan carries strategy and system.
const ACCENT = {
  1: { text: 'text-pyramid-1', dot: 'bg-pyramid-1', line: 'bg-pyramid-1', btn: 'btn-board btn-board-lime' },
  10: { text: 'text-pyramid-10', dot: 'bg-pyramid-10', line: 'bg-pyramid-10', btn: 'btn-board btn-board-orange' },
  19: { text: 'text-pyramid-19', dot: 'bg-pyramid-19', line: 'bg-pyramid-19', btn: 'btn-board btn-board-cyan' },
}

// Catalogue group -> board lane on /talk, which is also the lead source tag.
const LANE = {
  strategy: 'strategy',
  workshops: 'workshops',
  sdlc: 'sdlc',
  discovery: 'discovery',
  delivery: 'fde',
  recurring: 'advisory',
}

// The temporal shape of each engagement, drawn instead of only written.
// Keyed by group id and item index so the three locales share one drawing.
const SHAPES = {
  strategy: [{ type: 'span', weeks: 8 }],
  workshops: [
    { type: 'blocks', n: 3 },
    { type: 'span', weeks: 2 },
    { type: 'blocks', n: 2 },
    { type: 'blocks', n: 2 },
  ],
  sdlc: [{ type: 'scoped' }],
  discovery: [
    { type: 'span', weeks: 3 },
    { type: 'span', weeks: 4 },
  ],
  delivery: [
    { type: 'runway' },
    { type: 'span', weeks: 4 },
  ],
  recurring: [
    { type: 'cadence', n: 12 },
    { type: 'cadence', n: 4 },
    { type: 'cadence', n: 12 },
  ],
}

// Phase shapes for services sold as one program (drawn per phase card).
const PHASE_SHAPES = {
  strategy: [
    { type: 'blocks', n: 2 },
    { type: 'span', weeks: 8 },
    { type: 'cadence', n: 12 },
  ],
  sdlc: [
    { type: 'blocks', n: 2 },
    { type: 'span', weeks: 4 },
    { type: 'scoped' },
    { type: 'span', weeks: 2 },
    { type: 'cadence', n: 4 },
  ],
}

// Tiny authored diagrams, all in currentColor so they wear the group accent.
const ShapeStrip = ({ shape }) => {
  if (!shape) return null
  const H = 22
  if (shape.type === 'blocks') {
    return (
      <svg width={shape.n * 18 + 2} height={H} aria-hidden="true" className="shape-strip">
        {Array.from({ length: shape.n }).map((_, i) => (
          <rect key={i} x={i * 18 + 1} y={4} width={13} height={14} rx={2.5} fill="currentColor" opacity={0.9} />
        ))}
      </svg>
    )
  }
  if (shape.type === 'span') {
    const w = Math.min(shape.weeks, 12) * 11
    return (
      <svg width={w + 6} height={H} aria-hidden="true" className="shape-strip">
        <rect x={1} y={8} width={w} height={6} rx={3} fill="currentColor" opacity={0.35} />
        {Array.from({ length: Math.min(shape.weeks, 12) }).map((_, i) => (
          <rect key={i} x={i * 11 + 1} y={8} width={7} height={6} rx={2} fill="currentColor" opacity={0.9} />
        ))}
      </svg>
    )
  }
  if (shape.type === 'cadence') {
    return (
      <svg width={shape.n * 12 + 4} height={H} aria-hidden="true" className="shape-strip">
        <line x1={2} y1={11} x2={shape.n * 12} y2={11} stroke="currentColor" strokeWidth={1.5} opacity={0.3} />
        {Array.from({ length: shape.n }).map((_, i) => (
          <circle key={i} cx={i * 12 + 5} cy={11} r={3.2} fill="currentColor" opacity={0.9} />
        ))}
      </svg>
    )
  }
  if (shape.type === 'scoped') {
    return (
      <svg width={110} height={H} aria-hidden="true" className="shape-strip">
        <rect x={1} y={8} width={70} height={6} rx={3} fill="currentColor" opacity={0.9} />
        <rect x={74} y={8} width={34} height={6} rx={3} fill="none" stroke="currentColor" strokeWidth={1.4} strokeDasharray="4 3" opacity={0.7} />
      </svg>
    )
  }
  if (shape.type === 'runway') {
    return (
      <svg width={132} height={H} aria-hidden="true" className="shape-strip">
        <rect x={1} y={8} width={96} height={6} rx={3} fill="currentColor" opacity={0.9} />
        <path d="M100 11 L118 11" stroke="currentColor" strokeWidth={2} opacity={0.7} />
        <path d="M114 6 L122 11 L114 16" fill="none" stroke="currentColor" strokeWidth={2} opacity={0.9} />
      </svg>
    )
  }
  return null
}

// One authored emblem per group, stroke-drawn when the sheet swaps in.
const GLYPHS = {
  strategy: (
    <svg viewBox="0 0 96 96" className="h-full w-full" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <rect x="18" y="18" width="60" height="60" rx="6" className="glyph-stroke" />
        <path d="M48 18 V78 M18 48 H78" className="glyph-stroke" />
        <rect x="40" y="40" width="16" height="16" rx="3" transform="rotate(45 48 48)" className="glyph-stroke" />
      </g>
    </svg>
  ),
  workshops: (
    <svg viewBox="0 0 96 96" className="h-full w-full" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <rect x="12" y="34" width="20" height="16" rx="3" className="glyph-stroke" />
        <rect x="62" y="34" width="20" height="16" rx="3" className="glyph-stroke" />
        <rect x="37" y="58" width="20" height="16" rx="3" className="glyph-stroke" />
        <path d="M32 42 H60 M24 50 C24 62 30 66 37 66 M72 50 C72 62 64 66 57 66" className="glyph-stroke" />
      </g>
    </svg>
  ),
  sdlc: (
    <svg viewBox="0 0 96 96" className="h-full w-full" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="20" cy="48" r="7" className="glyph-stroke" />
        <circle cx="48" cy="48" r="7" className="glyph-stroke" />
        <circle cx="76" cy="48" r="7" className="glyph-stroke" />
        <path d="M27 48 H41 M55 48 H69" className="glyph-stroke" />
        <path d="M20 32 C34 18 62 18 76 32" className="glyph-stroke" strokeDasharray="4 4" />
        <path d="M72 62 L76 68 L82 58" className="glyph-stroke" />
      </g>
    </svg>
  ),
  discovery: (
    <svg viewBox="0 0 96 96" className="h-full w-full" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M22 72 V52 M38 72 V40 M54 72 V58" className="glyph-stroke" />
        <circle cx="58" cy="38" r="16" className="glyph-stroke" />
        <path d="M70 50 L82 62" className="glyph-stroke" />
      </g>
    </svg>
  ),
  delivery: (
    <svg viewBox="0 0 96 96" className="h-full w-full" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="48" cy="48" r="30" className="glyph-stroke" strokeDasharray="6 5" />
        <circle cx="48" cy="48" r="10" className="glyph-stroke" />
        <circle cx="48" cy="14" r="5" className="glyph-stroke" />
        <circle cx="78" cy="62" r="5" className="glyph-stroke" />
        <circle cx="18" cy="62" r="5" className="glyph-stroke" />
      </g>
    </svg>
  ),
  recurring: (
    <svg viewBox="0 0 96 96" className="h-full w-full" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M74 40 A28 28 0 1 0 78 56" className="glyph-stroke" />
        <path d="M74 26 V42 H58" className="glyph-stroke" />
        <circle cx="48" cy="48" r="4" className="glyph-stroke" />
      </g>
    </svg>
  ),
}

function useReveal(threshold = 0.1) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return undefined
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

// The selected work order, rendered as one sheet. `key` on the caller forces a
// fresh mount so pane-swap and the glyph stroke replay on every selection.
const SpecSheet = ({ group, item, shape, meta, onTalk }) => {
  const accent = ACCENT[group.accent]
  const ref = useRef(null)
  // The drawn devices (title-line, glyph strokes, shape strips) transition in
  // under an .is-visible ancestor; adding it a frame after mount replays the
  // draw on every selection, since the caller keys this component.
  useEffect(() => {
    // Applied in an effect (post-paint) so the transition runs; deliberately
    // not rAF, which never fires in background tabs and would leave the
    // sheet invisible there.
    if (ref.current) ref.current.classList.add('is-visible')
  }, [])
  return (
    <article ref={ref} className="pane-swap relative overflow-hidden rounded-2xl border border-white/10 bg-[#151b23] p-7 md:p-10">
      <div className={`pointer-events-none absolute -right-4 -top-4 h-36 w-36 opacity-[0.14] md:h-44 md:w-44 ${accent.text}`}>
        {GLYPHS[group.id]}
      </div>

      <p className={`text-xs uppercase tracking-[0.35em] ${accent.text}`}>{group.kicker}</p>
      <h2 className="mt-3 max-w-xl text-2xl font-light text-white md:text-[2.1rem] md:leading-[1.15]">{item.name}</h2>
      <span className={`title-line mt-4 block h-0.5 w-16 rounded-full ${accent.line}`} />

      <p className="mt-5 max-w-2xl text-[17px] leading-8 text-slate-300">{item.body}</p>

      {item.phases ? (
        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{meta.phases}</p>
          <ol className="mt-4 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {item.phases.map((ph, pi) => (
              <li key={ph.name} className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3.5">
                <p className={`text-[11px] font-semibold tabular-nums ${accent.text}`}>{String(pi + 1).padStart(2, '0')}</p>
                <p className="mt-1 text-[13.5px] leading-5 text-slate-200">{ph.name}</p>
                <p className="mt-1.5 text-xs text-slate-400">{ph.duration}</p>
                <span className={`mt-2 block ${accent.text}`}>
                  <ShapeStrip shape={(PHASE_SHAPES[group.id] || [])[pi]} />
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <dl className="mt-8 max-w-2xl divide-y divide-white/[0.07] border-y border-white/[0.07]">
        <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:gap-6">
          <dt className="w-44 shrink-0 text-sm text-slate-400">{meta.duration}</dt>
          <dd className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[15px] font-light text-slate-200">
            {item.duration}
            <span className={accent.text}><ShapeStrip shape={shape} /></span>
          </dd>
        </div>
        <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:gap-6">
          <dt className="w-44 shrink-0 text-sm text-slate-400">{meta.leave}</dt>
          <dd className={`text-[15px] font-medium ${accent.text}`}>{item.leave}</dd>
        </div>
        {item.next ? (
          <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:gap-6">
            <dt className="w-44 shrink-0 text-sm text-slate-400">{meta.next}</dt>
            <dd className="text-[15px] font-light text-slate-300">{item.next}</dd>
          </div>
        ) : null}
      </dl>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a href={`/talk?i=${LANE[group.id]}`} onClick={onTalk} className={accent.btn}>
          {meta.talk}
          <span aria-hidden="true">&#8594;</span>
        </a>
        {group.cta ? (
          <a
            href={group.ctaHref || '/forward-deployed-engineering'}
            onClick={() => trackEvent('services_fde_click')}
            className={`inline-flex items-center gap-2 px-1 text-sm font-semibold ${accent.text}`}
          >
            {group.cta}
            <span aria-hidden="true">&#8594;</span>
          </a>
        ) : null}
      </div>
    </article>
  )
}

const ServicesCatalogPage = () => {
  const { i18n } = useTranslation()
  const c = getServicesCatalogContent(i18n.language)
  const bookLabel = getCtaContent(i18n.language).close.book
  const heroRef = useReveal(0.05)

  // Flat index over every service, so selection and keyboard travel are one
  // dimension even though the rail reads as six groups.
  const flat = useMemo(
    () => c.groups.flatMap((g, gi) => g.items.map((item, ii) => ({ g, gi, item, ii, key: `${g.id}-${ii}` }))),
    [c]
  )

  // A targeted arrival names a service (?s=key) or a group (#strategy, the
  // form the home portfolio cards use); both select and silence the
  // self-advance, and the workbench scrolls into view.
  const targetFromLocation = () => {
    if (typeof window === 'undefined') return null
    const wanted = new URLSearchParams(window.location.search).get('s')
    if (wanted && flat.some((f) => f.key === wanted)) return wanted
    const hash = (window.location.hash || '').replace('#', '')
    if (hash) {
      if (flat.some((f) => f.key === hash)) return hash
      const groupFirst = flat.find((f) => f.g.id === hash)
      if (groupFirst) return groupFirst.key
    }
    return null
  }
  const arrivedTargeted = targetFromLocation() != null

  const [selKey, setSelKey] = useState(() => targetFromLocation() || flat[0].key)

  useEffect(() => {
    if (!arrivedTargeted || typeof window === 'undefined') return
    const el = document.getElementById('workbench')
    if (el) el.scrollIntoView({ block: 'start' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Until the visitor touches the bench, it demonstrates itself: the sheet
  // advances through the catalogue on a slow cadence. Any pointer or key on
  // the workbench hands control over for good.
  const [autoPlay, setAutoPlay] = useState(!arrivedTargeted)
  const stopAuto = useCallback(() => setAutoPlay(false), [])
  useEffect(() => {
    if (!autoPlay) return undefined
    if (typeof window === 'undefined') return undefined
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      setSelKey((prev) => {
        const idx = flat.findIndex((f) => f.key === prev)
        return flat[(idx + 1) % flat.length].key
      })
    }, 3500)
    return () => clearInterval(t)
  }, [autoPlay, flat])
  // Desktop always shows a sheet; the mobile accordion may be fully closed,
  // so it reads the raw key while desktop falls back to the first service.
  const sel = flat.find((f) => f.key === selKey) || flat[0]

  const [swapKey, setSwapKey] = useState(0)

  const select = useCallback((key) => {
    setAutoPlay(false)
    setSwapKey((k) => k + 1)
    setSelKey(key)
    trackEvent('services_spec_view', { service: key })
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('s', key)
      window.history.replaceState(null, '', url)
    }
  }, [])

  const deselect = useCallback(() => {
    setAutoPlay(false)
    setSelKey('')
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.delete('s')
      window.history.replaceState(null, '', url)
    }
  }, [])

  // Hovering a row previews it on the stage after a short dwell; clicking
  // commits it (URL + analytics). Both take over from the auto-advance.
  const hoverTimer = useRef(null)
  const previewOnHover = useCallback((key) => {
    setAutoPlay(false)
    clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => setSelKey(key), 140)
  }, [])
  const cancelHover = useCallback(() => clearTimeout(hoverTimer.current), [])
  useEffect(() => () => clearTimeout(hoverTimer.current), [])

  // Arrow keys travel the whole catalogue while focus is on the rail.
  const onRailKeyDown = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const idx = flat.findIndex((f) => f.key === sel.key)
    const next = flat[(idx + (e.key === 'ArrowDown' ? 1 : flat.length - 1)) % flat.length]
    select(next.key)
    const btn = document.getElementById(`svc-${next.key}`)
    if (btn) btn.focus()
  }

  const railGroups = (
    <nav aria-label="Service catalogue" onKeyDown={onRailKeyDown} className="space-y-5">
      {c.groups.map((g) => {
        const accent = ACCENT[g.accent]
        return (
          <div key={g.id}>
            <p className={`flex items-center gap-2.5 text-xs uppercase tracking-[0.3em] ${accent.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
              {g.kicker}
            </p>
            <ul className="mt-2.5">
              {g.items.map((item, ii) => {
                const key = `${g.id}-${ii}`
                const active = key === sel.key
                return (
                  <li key={key}>
                    <button
                      id={`svc-${key}`}
                      type="button"
                      onClick={() => select(key)}
                      onMouseEnter={() => previewOnHover(key)}
                      onMouseLeave={cancelHover}
                      aria-current={active ? 'true' : undefined}
                      title={item.name}
                      className={`group flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-[7px] text-left text-[15px] transition-colors duration-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-white/50 ${
                        active ? `bg-white/[0.05] ${accent.text}` : 'text-slate-400 hover:bg-white/[0.025] hover:text-white'
                      }`}
                    >
                      <span
                        className={`h-px shrink-0 rounded-full transition-all duration-300 ${accent.line} ${
                          active ? 'w-5 opacity-100' : 'w-2 opacity-40 group-hover:w-5 group-hover:opacity-100'
                        }`}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 truncate font-light">{item.name}</span>
                      <span
                        className={`shrink-0 text-sm transition-all duration-200 ${
                          active
                            ? `translate-x-0 opacity-100 ${accent.text}`
                            : '-translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-60'
                        }`}
                        aria-hidden="true"
                      >
                        &#8250;
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </nav>
  )

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/services" />

      {/* hero: short, then straight to the bench */}
      <header className="px-6 pt-32 md:px-16 md:pt-40">
        <div ref={heroRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>

          {/* the service record, one quiet ledger line instead of stat tiles */}
          <p className="mt-8 border-t border-white/[0.08] pt-5 text-[13.5px] leading-7 text-slate-400">
            {c.proof.numbers.map((n, i) => (
              <span key={n.value}>
                <span className="whitespace-nowrap">
                  <span className="font-semibold text-slate-200">{n.value}</span> {n.label}
                </span>{' '}
                {i < c.proof.numbers.length - 1 ? <span className="mx-1.5 text-slate-600" aria-hidden="true">&middot;{' '}</span> : null}
              </span>
            ))}
            <span className="mx-1.5 text-slate-600" aria-hidden="true">&middot;</span>{' '}
            <span className="whitespace-normal">{c.proof.names}</span>
          </p>
        </div>
      </header>

      {/* the workbench */}
      <main id="workbench" className="scroll-mt-24 px-6 pb-24 pt-12 md:px-16 md:pt-16">
        <div className="mx-auto max-w-6xl">
          {/* lg+: console. rail left, stage right */}
          <div className="hidden gap-12 lg:grid lg:grid-cols-[290px_minmax(0,1fr)]">
            <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:self-start lg:overflow-y-auto lg:pr-1">{railGroups}</div>
            <div aria-live="polite" onPointerDown={stopAuto} className="lg:min-h-[51rem]">
              <SpecSheet
                key={swapKey}
                group={sel.g}
                item={sel.item}
                shape={(SHAPES[sel.g.id] || [])[sel.ii]}
                meta={c.meta}
                onTalk={() => trackEvent(`services_talk_${sel.g.id}`)}
              />
            </div>
          </div>

          {/* below lg: same catalogue, the sheet unfolds under its own row */}
          <div className="lg:hidden">
            <div className="space-y-8">
              {c.groups.map((g) => {
                const accent = ACCENT[g.accent]
                return (
                  <section key={g.id}>
                    <p className={`flex items-center gap-2.5 text-xs uppercase tracking-[0.3em] ${accent.text}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
                      {g.kicker}
                    </p>
                    <ul className="mt-3 divide-y divide-white/[0.06] border-y border-white/[0.06]">
                      {g.items.map((item, ii) => {
                        const key = `${g.id}-${ii}`
                        const active = key === selKey
                        return (
                          <li key={key}>
                            <button
                              type="button"
                              onClick={() => (active ? deselect() : select(key))}
                              aria-expanded={active}
                              className={`flex w-full items-center gap-3 py-3.5 text-left text-[15px] transition-colors ${
                                active ? 'text-white' : 'text-slate-300'
                              }`}
                            >
                              <span className="min-w-0 flex-1 font-light">{item.name}</span>
                              <span className="max-w-[38%] shrink-0 truncate text-xs text-slate-500">{item.duration}</span>
                              <span
                                className={`shrink-0 transition-transform duration-200 ${accent.text} ${active ? 'rotate-90' : ''}`}
                                aria-hidden="true"
                              >
                                &#8250;
                              </span>
                            </button>
                            {active ? (
                              <div className="pb-5">
                                <SpecSheet
                                  key={key}
                                  group={g}
                                  item={item}
                                  shape={(SHAPES[g.id] || [])[ii]}
                                  meta={c.meta}
                                  onTalk={() => trackEvent(`services_talk_${g.id}`)}
                                />
                              </div>
                            ) : null}
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                )
              })}
            </div>
          </div>

          {/* the close */}
          <section className="mt-20 border-t border-white/[0.08] pt-12 text-center">
            <h2 className="text-2xl font-light md:text-3xl">{c.footerCta.title}</h2>
            <p className="mx-auto mt-3 max-w-xl leading-7 text-slate-400">{c.footerCta.body}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href="/talk?i=discovery"
                onClick={() => trackEvent('services_talk_click')}
                className="inline-flex items-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-7 py-3 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5"
              >
                {c.footerCta.cta}
                <span aria-hidden="true">&#8594;</span>
              </a>
            <a
              href="/book"
              onClick={() => trackEvent('services_book_click')}
              className="inline-flex items-center gap-2 rounded-[10px] border border-pyramid-19/50 px-7 py-3 font-semibold text-pyramid-19 transition-all hover:bg-pyramid-19/10"
            >
              {bookLabel}
              <span aria-hidden="true">&#8594;</span>
            </a>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

export default ServicesCatalogPage
